import { IngredientType } from '../enums/IngredientType';

export const UNITS_BY_TYPE = {
  [IngredientType.Solid]: [
    'g',
    'kg',
    'lb',
    'oz',
    'unit',
    'piece',
    'cup',
    'tablespoon',
    'teaspoon',
    'slice',
  ],
  [IngredientType.Liquid]: ['ml', 'L', 'fl_oz', 'cup', 'tablespoon', 'teaspoon'],
  [IngredientType.Spice]: ['pinch', 'to_taste', 'other'],
} as const satisfies Record<IngredientType, readonly string[]>;

export type IngredientUnit =
  (typeof UNITS_BY_TYPE)[IngredientType][number];

export function getUnitsForType(type: IngredientType): readonly string[] {
  return UNITS_BY_TYPE[type];
}

export function isValidUnit(type: IngredientType, unit: string): boolean {
  return (UNITS_BY_TYPE[type] as readonly string[]).includes(unit);
}

export interface QuantityUnitValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Shared by the personal and group planner ingredient-entry services so the
 * positive-quantity / unit-for-type rule can't drift between the two.
 */
export function validateQuantityAndUnit(
  quantity: string | null | undefined,
  unit: string | null | undefined,
  ingredientType: IngredientType | undefined,
): QuantityUnitValidationResult {
  if (quantity !== undefined && quantity !== null) {
    const parsed = parseFloat(quantity);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return { valid: false, error: 'Quantity must be a positive number' };
    }
  }
  if (unit !== undefined && unit !== null && ingredientType !== undefined) {
    if (!isValidUnit(ingredientType, unit)) {
      return { valid: false, error: `Unit "${unit}" is not valid for this ingredient's type` };
    }
  }
  return { valid: true };
}

/**
 * Normalizes ingredient name for search and uniqueness: trim, lowercase, strip accents (NFD).
 * Backend persists DB-normalized form using PostgreSQL unaccent; this is the app-layer equivalent.
 */
export function normalizeIngredientName(name: string): string {
  return name
    .trim()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase();
}
